'use client';
/**
 * @file useSportCategory.ts
 * @description Custom hook for managing the Sports Category view.
 * Refactored to orchestrate specialized sub-hooks for modularity and maintainability.
 */

import { useEffect, useState } from 'react';
import { useParams } from 'next/navigation';

import { setSelectedSidebarItem, useAppDispatch, useAppSelector } from '@/store';
import { SidebarItem } from '@/types';
import { createMarketFromGame } from '@/utils';
import { useSportCategoryData } from './useSportCategoryData';
import { useSportCategoryGames } from './useSportCategoryGames';
// --- Sub-hooks ---
import { useSportCategorySearch } from './useSportCategorySearch';
import { useSportCategoryTrading } from './useSportCategoryTrading';
import { useSportsSidebar } from './useSportsSidebar';

// --- Constants & Fallbacks ---
const liveFallback = {
  sport: 'live',
  category: 'Live',
  type: 'automated',
  leagues: [],
  totalEvents: 0,
} as SidebarItem;

/**
 * Custom hook for managing the Sports Category view.
 */
export const useSportCategory = () => {
  const params = useParams();
  const sportSlug = (params.slug as string)?.toLowerCase();
  const gameId = params.id as string;
  const dispatch = useAppDispatch();

  // --- 1. Base State Retrieval ---
  const { state: sidebarState } = useSportsSidebar();
  const bookmarkedEventIds = useAppSelector((s) => s.bookmarks.bookmarkedEventIds);
  const { currentCategoryData, isLoading: isSidebarInitialLoading } = sidebarState;
  const effectiveSportSlug = sportSlug || 'live';

  // --- 2. Search Logic ---
  const { searchQuery, debouncedSearchQuery, setSearchQuery } = useSportCategorySearch();

  // --- 3. Shareable UI State ---
  const [activeTab, setActiveTab] = useState<'games' | 'props'>(() => (gameId ? 'props' : 'games'));
  const [showSpreadsAndTotals, setShowSpreadsAndTotals] = useState(true);

  /**
   * We need a stable reference for resetting selections during category transitions.
   * This bridges the Data hook and the Trading hook.
   */
  const [_, setActiveMarketData] = useState<any>(null);
  const resetSelections = () => setActiveMarketData(null);

  // --- 4. Metadata & Category Transition Logic ---
  const { series_id, tag_id, hasGames, showComments } = useSportCategoryData({
    currentCategoryData,
    activeId: sidebarState.activeId,
    effectiveSportSlug,
    gameId,
    activeTab,
    setActiveTab,
    resetSelections,
  });

  // --- 5. Data Fetching (Games & Props) ---
  const games = useSportCategoryGames({
    tag_id,
    series_id,
    effectiveSportSlug,
    debouncedSearchQuery,
    activeTab,
    gameId,
    showSpreadsAndTotals,
  });

  // --- 6. Trading & Interaction logic ---
  const trading = useSportCategoryTrading({
    selectedEvent: games.selectedEvent,
    effectiveSportSlug,
    bookmarkedEventIds,
    apiEvents: games.apiEvents,
  });

  // Sync selected sidebar item in Redux
  useEffect(() => {
    if (currentCategoryData) {
      dispatch(setSelectedSidebarItem(currentCategoryData));
    } else if (!isSidebarInitialLoading && !sportSlug) {
      dispatch(setSelectedSidebarItem(liveFallback));
    }
  }, [currentCategoryData, dispatch, isSidebarInitialLoading, sportSlug]);

  return {
    state: {
      activeTab,
      tradeModalState: trading.tradeModalState,
      gamesByDate: games.gamesByDate,
      isDesktop: trading.isDesktop,
      sportSlug: effectiveSportSlug,
      games: games.apiEvents,
      selectedEvent: games.selectedEvent,
      sidebarData:
        trading.activeMarketData ||
        (games.selectedEvent
          ? {
              ...createMarketFromGame(games.selectedEvent, 1, effectiveSportSlug),
              event: games.selectedEvent,
              ...trading.getEventResolution(games.selectedEvent),
            }
          : null),
      selectedSide: trading.selectedSide,
      sidebarTab: trading.sidebarTab,
      isSidebarLoading: isSidebarInitialLoading,
      isCurrentSportPropsLoading: games.isPropsLoading || games.isGameLoading,
      isLoading: games.isPropsLoading || games.isGameLoading,
      activeId: sidebarState.activeId,
      isBookmarked: trading.isBookmarked,
      columnVisibility: games.columnVisibility,
      propsEvents: games.propsEvents,
      searchQuery,
      debouncedSearchQuery,
      isGameDataLoading: games.isGameLoading,
      showSpreadsAndTotals,
      hasSpreadRaw: games.hasSpreadMarkets,
      hasTotalRaw: games.hasTotalMarkets,
      hasGames,
      hasProps: games.hasProps,
      seriesId: series_id,
      tagId: tag_id,
      showComments,
      fetchNextPage: games.fetchNextPage,
      hasNextPage: games.hasNextPage,
      isFetchingNextPage: games.isFetchingNextPage,
    },
    handlers: {
      setActiveTab,
      handleGoBack: trading.handleCloseTradeModal,
      handleTeamClick: trading.handleTeamClick,
      handleCloseTradeModal: trading.handleCloseTradeModal,
      handlePropTradeClick: trading.handlePropTradeClick,
      handleBookmarkClick: trading.handleBookmarkClick,
      setSelectedSide: trading.setSelectedSide,
      setSidebarTab: trading.setSidebarTab,
      setShowSpreadsAndTotals,
      handleSearchChange: setSearchQuery,
    },
  };
};
